import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Editor from '@/components/Froala/Editor';
import { LIMIT_DURATION_FOR_COMMENT_EDIT, LIMIT_DURATION_FOR_COMMENT_EDIT_IN_MINUTES } from '@/configs/dybr';
import { selectFamiliarProfiles } from '@/store/lists/selectors/selectFamiliarProfiles';
import { useActiveProfile, useTokenState } from '@/store/localStorage/useTokenState';
import { selectActiveProfileAugmented } from '@/store/user/selectors/selectActiveProfileAugmented';
import isCommentEditTimeOut from '@/utils/helpers/commentEdit';
import { debounce } from '@/utils/helpers/general';
import { getAvatar } from '@/utils/helpers/profiles';
import useInterval from '@/utils/hooks/useInterval';

import { useSelector } from 'react-redux';
import { useEntryPageContext } from '@/scenes/Blog/BlogEntryPage/EntryPageContext';
import css from './MessageEditForm.module.css';

const TimeLeft = ({ limitMin, since }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useInterval(() => {
    const secondsSincePublished = moment().diff(moment(since), 'seconds');
    setTimeLeft(limitMin * 60 - secondsSincePublished);
  }, 1000 / 60);
  return timeLeft > 0
    ? 'время для редактирования: ' + moment.utc(timeLeft * 1000).format('m:ss')
    : 'время для редактирования вышло';
};

export function MessageEditForm({
  editingComment,
  commentDraft,
  navigateTo,
  onPublishFinished,
  onCreateComment,
  onUpdateComment,
  onSaveDraftComment,
  sendButtonLabel
}) {
  const navigate = useNavigate();
  const [token] = useTokenState();
  const { activeProfileId } = useActiveProfile();
  const profile = useSelector(state => selectActiveProfileAugmented(state, { activeProfileId }));
  const familiarProfiles = useSelector(selectFamiliarProfiles);
  const { editorRef, formRef } = useEntryPageContext();
  const [commentContent, setCommentContent] = useState(commentDraft?.content || '');
  const [submitting, setSubmitting] = useState(false);
  const [publishingRequested, setPublishingRequested] = useState(false);

  useEffect(() => {
    const saveInterval = setInterval(() => {
      const editor = editorRef.current;
      if (editor && editor.codeView && editor.codeView.isActive()) {
        const content = editor.codeView.get();
        onSaveDraftComment(content);
      }
    }, 1000);

    return () => {
      if (saveInterval) {
        clearInterval(saveInterval);
      }
    };
  }, [editorRef]);

  const saveDraft = () => debounce(500, onSaveDraftComment);

  const handleContentChange = value => {
    setCommentContent(value);
    // this works only when outside of html editing, because froala does not fire any event in html mode
    saveDraft()(value || '');
  };

  useEffect(() => {
    const publishComment = async () => {
      setSubmitting(true);

      const editor = editorRef.current;

      let content = editor.html.get();

      // transform html and use it if the save button was hit from the code view
      if (editor.codeView && editor.codeView.isActive()) {
        editor.html.set(editor.codeView.get());
        content = editor.codeView.get();
      }

      let res;
      if (editingComment) {
        const minutesSincePublished = moment().diff(moment(editingComment.createdAt), 'minutes');
        if (isCommentEditTimeOut(minutesSincePublished - 1)) {
          window.alert(
            `Закончились ${LIMIT_DURATION_FOR_COMMENT_EDIT_IN_MINUTES} минут, в течение которых можно редактировать комментарий.`
          );
          onPublishFinished();
          return;
        }

        res = await onUpdateComment(content);
      } else {
        res = await onCreateComment(content);
      }
      setSubmitting(false);
      if (res.error) {
        window.alert(res.error);
        return;
      }
      setCommentContent('');
      await onPublishFinished();
      onSaveDraftComment('');
      const commentId = res.data?.id ?? '';
      navigate(navigateTo(commentId));
    };

    if (publishingRequested) {
      publishComment();
      setPublishingRequested(false);
    }
  }, [publishingRequested, editingComment, editorRef, onPublishFinished, profile.id, token]);

  return (
    <div ref={formRef} className={`${css.editor} ${css['entry-form']}`}>
      <div className={css.editorContainer}>
        <Editor
          value={commentContent}
          hideToolbar={true}
          onChange={handleContentChange}
          onInit={editor => {
            editorRef.current = editor;
          }}
          onSubmitByEnter={() => setPublishingRequested(true)}
          autocompleteMentionOptions={familiarProfiles}
        />
      </div>
      <p className={css['edit-entry-form-buttons']}>
        <div className={css['editor-meta--up']}>
          <div className={css['editor-avatar']} style={{ position: 'relative' }}>
            <img src={getAvatar(profile)} alt={profile.settings?.nickname ?? ''} />
          </div>
          <div className={css['editor-author-wrapping']}>
            <div className={css['editor-author']}>
              <Link to={`/profile/${profile.id}`}>{profile.nickname}</Link>
            </div>
            {profile.subtext && (
              <div className={css['editor-subtext']}>
                <span>{profile.settings?.subtext ?? ''}</span>
              </div>
            )}
          </div>

          {editingComment && (
            <div className={css['editor-date']}>
              {LIMIT_DURATION_FOR_COMMENT_EDIT && (
                <TimeLeft since={editingComment.createdAt} limitMin={LIMIT_DURATION_FOR_COMMENT_EDIT_IN_MINUTES} />
              )}
            </div>
          )}
        </div>
        <button
          disabled={submitting || commentContent.length === 0}
          onClick={() => setPublishingRequested(true)}
          className={css['add-entry-btn']}
          data-testid="blog-entry-comment-button"
        >
          {submitting
            ? 'публикуем...'
            : editingComment
            ? 'сохранить'
            : sendButtonLabel
            ? sendButtonLabel
            : 'комментировать'}
        </button>
      </p>
    </div>
  );
}
